import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DollarSign, ArrowRight, Sparkles, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CostSavingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PricingItem {
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  color: string;
  isCurrent?: boolean;
  isFree?: boolean;
  label?: string;
}

const pricingData: PricingItem[] = [
  { name: 'DAT', monthlyPrice: 149, yearlyPrice: 1788, color: '#ef4444' },
  { name: 'Truckstop', monthlyPrice: 129, yearlyPrice: 1548, color: '#f97316' },
  { name: 'LaneRunner', monthlyPrice: 49.99, yearlyPrice: 600, color: '#1ab7aa', label: '(Future)' },
  { name: 'LaneRunner NOW', monthlyPrice: 0, yearlyPrice: 0, color: '#F7CF0D', isCurrent: true, isFree: true, label: '(Early Access)' },
];

const maxYearly = Math.max(...pricingData.map(p => p.yearlyPrice));

const CostSavingsDialog: React.FC<CostSavingsDialogProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [animatedWidths, setAnimatedWidths] = useState<number[]>([0, 0, 0, 0]);

  useEffect(() => {
    if (open) {
      // Stagger the bar animations
      pricingData.forEach((item, index) => {
        setTimeout(() => {
          setAnimatedWidths(prev => {
            const newWidths = [...prev];
            newWidths[index] = item.yearlyPrice === 0 ? 3 : (item.yearlyPrice / maxYearly) * 100;
            return newWidths;
          });
        }, 150 * (index + 1));
      });
    } else {
      setAnimatedWidths([0, 0, 0, 0]);
    }
  }, [open]);

  const handleClaimAccess = () => {
    onOpenChange(false);
    navigate('/pricing');
  };

  const maxSavings = pricingData[0].yearlyPrice; // DAT is the highest

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-white border-gray-200">
        <DialogHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-2">
            <DollarSign className="h-6 w-6 text-[#1ab7aa]" />
            <DialogTitle className="text-xl sm:text-2xl text-gray-900">
              Annual Load Board Cost Comparison
            </DialogTitle>
          </div>
          <DialogDescription className="text-gray-500">
            See how much you could save by switching to LaneRunner
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {pricingData.map((item, index) => (
            <div key={item.name} className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${item.isCurrent ? 'text-[#1ab7aa]' : 'text-gray-700'}`}>
                    {item.name}
                  </span>
                  {item.label && (
                    <span className="text-xs text-gray-400">{item.label}</span>
                  )}
                  {item.isFree && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F7CF0D]/20 text-[#B8960A] text-xs font-medium">
                      <Sparkles className="h-3 w-3" />
                      FREE
                    </span>
                  )}
                </div>
                <span className={`font-semibold ${item.isFree ? 'text-[#1ab7aa]' : 'text-gray-900'}`}>
                  {item.isFree ? '$0/year' : `$${item.yearlyPrice.toLocaleString()}/year`}
                </span>
              </div>
              <div className="h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                <div
                  className="h-full rounded-lg transition-all duration-700 ease-out flex items-center justify-end pr-3"
                  style={{
                    width: `${animatedWidths[index]}%`,
                    backgroundColor: item.color,
                    minWidth: item.isFree ? '40px' : undefined,
                  }}
                >
                  {!item.isFree && animatedWidths[index] > 20 && (
                    <span className="text-xs font-medium text-white">
                      ${item.monthlyPrice}/mo
                    </span>
                  )}
                </div>
                {item.isFree && (
                  <div className="absolute inset-0 flex items-center pl-3">
                    <span className="text-xs font-bold text-gray-800">$0/mo</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Savings Callout */}
        <div className="bg-gradient-to-r from-[#1ab7aa]/10 to-[#F7CF0D]/10 rounded-xl p-4 border border-[#1ab7aa]/20">
          <div className="flex items-center justify-center gap-2 mb-1">
            <TrendingDown className="h-5 w-5 text-[#1ab7aa]" />
            <span className="text-lg font-bold text-gray-900">
              Save up to ${maxSavings.toLocaleString()}/year
            </span>
          </div>
          <p className="text-center text-sm text-gray-600">
            Compared to traditional load boards — that's <span className="font-semibold text-[#1ab7aa]">${Math.round(maxSavings / 12)}/month</span> back in your pocket
          </p>
        </div>

        {/* CTA Button */}
        <Button
          onClick={handleClaimAccess}
          className="w-full bg-[#1ab7aa] hover:bg-[#1ab7aa]/90 text-white font-semibold py-6 text-lg rounded-lg transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-[#1ab7aa]/20"
        >
          Claim Your Free Access
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>

        <p className="text-center text-xs text-gray-400">
          Early access members keep special benefits
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default CostSavingsDialog;
